import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, CheckCircle, Sparkle, CircleNotch, Envelope } from "@phosphor-icons/react";

const USE_CASES = [
  { key: "founder-solo",       label: "Founder building in public" },
  { key: "coach-consultant",   label: "Coach or consultant growing inbound" },
  { key: "creator-full-time",  label: "Full-time creator / educator" },
  { key: "exec-thought-lead",  label: "Executive building thought leadership" },
  { key: "agency-team",        label: "Agency or team managing a personal brand" },
  { key: "other",              label: "Something else" },
];

const PRO_HIGHLIGHTS = [
  "Unlimited drafts, comment logs and comment suggestions",
  "Unlimited large-file uploads in the Knowledge Base",
  "Priority AI queue with faster response times",
  "Early access to new modules as they ship",
];

/**
 * WaitlistModal — captures Pro-upgrade interest while Stripe is disabled.
 * Backend endpoint: POST /api/waitlist/pro (idempotent).
 */
export default function WaitlistModal({ open, onClose }) {
  const [useCase, setUseCase] = useState("founder-solo");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // {on_waitlist, joined_at, use_case}
  const [loading, setLoading] = useState(true);
  const [justJoined, setJustJoined] = useState(false);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/waitlist/status");
        if (alive) {
          setStatus(r.data);
          if (r.data.use_case) setUseCase(r.data.use_case);
        }
      } catch {} finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/waitlist/pro", { use_case: useCase, note: note.trim() });
      setStatus({ on_waitlist: true, joined_at: r.data.joined_at, use_case: useCase });
      setPosition(r.data.position || null);
      setJustJoined(true);
      toast.success(r.data.already_on ? "Your spot is saved." : "You're on the Pro waitlist.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const close = () => {
    setJustJoined(false);
    setNote("");
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="waitlist-backdrop"
    >
      <div className="card w-full max-w-xl p-0 fade-up flex flex-col" data-testid="waitlist-modal" style={{ maxHeight: "88vh" }}>
        <div className="flex items-start justify-between gap-4 p-6 pb-3 shrink-0">
          <div>
            <div className="eyebrow mb-1 flex items-center gap-1.5" style={{ color: "var(--accent-2)" }}>
              <Sparkle size={12} weight="fill"/> Pro is coming
            </div>
            <h3 className="font-serif text-2xl font-semibold">Get on the list. Skip the wait.</h3>
            <p className="text-sm mt-1" style={{ color: "var(--ink-3)" }}>
              We're finalizing billing. Join the waitlist and you'll get an email the moment Pro opens.
            </p>
          </div>
          <button type="button" onClick={close} data-testid="waitlist-close" className="p-2 rounded-full hover:bg-[color:var(--paper)]" aria-label="Close"><X size={16}/></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto scroll-thin px-6 pb-4">
          {loading ? (
            <div className="text-sm py-6 text-center" style={{ color: "var(--ink-3)" }}><CircleNotch size={14} className="animate-spin inline mr-2"/> Checking your status…</div>
          ) : (status?.on_waitlist && !justJoined) ? (
            <div className="text-center py-6" data-testid="waitlist-already">
              <CheckCircle size={26} weight="fill" style={{ color: "var(--accent)" }} className="mx-auto mb-3"/>
              <div className="font-serif text-2xl mb-1">You're already on the list.</div>
              <p className="text-sm max-w-sm mx-auto" style={{ color: "var(--ink-2)" }}>
                We saved your spot {status.joined_at ? new Date(status.joined_at).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : "recently"}. We'll email you the moment Pro opens.
              </p>
              {status.use_case && (
                <p className="text-xs mt-3 font-mono" style={{ color: "var(--ink-3)" }}>Use case: {USE_CASES.find((u) => u.key === status.use_case)?.label || status.use_case}</p>
              )}
              <button onClick={close} className="btn btn-ghost mt-6">Close</button>
            </div>
          ) : justJoined ? (
            <div className="text-center py-6" data-testid="waitlist-confirmed">
              <CheckCircle size={26} weight="fill" style={{ color: "var(--accent)" }} className="mx-auto mb-3"/>
              <div className="font-serif text-2xl mb-1">You're on the list.</div>
              <p className="text-sm max-w-sm mx-auto" style={{ color: "var(--ink-2)" }}>
                We'll email you the moment Pro opens{position ? `. You're #${position} on the list.` : "."}
              </p>
              <div className="text-xs mt-3 font-mono flex items-center justify-center gap-1.5" style={{ color: "var(--ink-3)" }}>
                <Envelope size={11}/> Watch for a message from BrandLoop.
              </div>
              <button onClick={close} className="btn btn-accent mt-6" data-testid="waitlist-done">Back to work</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="waitlist-form">
              <div className="rounded-2xl p-4 mb-5" style={{ background: "var(--bg-soft)", border: "1px solid var(--line-2)" }}>
                <div className="eyebrow mb-2" style={{ color: "var(--accent-2)" }}>What Pro unlocks</div>
                <ul className="text-sm space-y-1.5" style={{ color: "var(--ink-2)" }}>
                  {PRO_HIGHLIGHTS.map((h, i) => (
                    <li key={i} className="flex gap-2 items-start"><CheckCircle size={12} weight="fill" style={{ color: "var(--accent)", marginTop: 4, flexShrink: 0 }}/><span>{h}</span></li>
                  ))}
                </ul>
              </div>

              <label className="block mb-4">
                <div className="eyebrow mb-2">What's your primary use case?</div>
                <select className="select" data-testid="waitlist-use-case" value={useCase} onChange={(e) => setUseCase(e.target.value)}>
                  {USE_CASES.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                </select>
              </label>

              <label className="block mb-5">
                <div className="eyebrow mb-2">Anything you want us to know? (optional)</div>
                <textarea
                  data-testid="waitlist-note"
                  className="textarea"
                  rows={3}
                  placeholder="What would make Pro a no-brainer for you?"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>

              <div className="flex justify-end gap-3">
                <button type="button" onClick={close} className="btn btn-ghost">Maybe later</button>
                <button type="submit" disabled={busy} data-testid="waitlist-submit" className="btn btn-accent">
                  {busy ? <><CircleNotch size={13} className="animate-spin"/> Joining…</> : <><Sparkle size={13} weight="fill"/> Join the waitlist</>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
